import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, MessageSquare, CheckCircle2, AlertCircle, Clock,
  Star, User, ShieldCheck, Loader2,
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getAppraisalDetails, submitAppraisalReview, saveAppraisalDraft } from '@/services/performanceService';
import { draftFromForm, formFromDraft } from '../shared/reviewDraft';
import { useAuth } from '@/contexts/AuthContext';
import {
  AppraisalHeader,
  EmployeeInfoCard,
  StatusBanner,
  SectionCard,
  QuestionEditor,
  QuestionDisplay,
  FinalCommentCard,
  RatingBadge,
  StarRating,
} from '../shared';

const STEPS = ["Self Review", "Approval Review", "Employee Comment", "Admin Review", "Completed"];

function EmployeeAppraisalEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [appraisal, setAppraisal] = useState(null);
  const [template, setTemplate] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const submitLockRef = useRef(false);

  // Form state
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [starScores, setStarScores] = useState({});
  const [employeeComment, setEmployeeComment] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  // This browser's copy of the form, so a refresh or a killed app doesn't lose
  // typing. It lasts until they leave the page; Save Draft is what keeps it.
  const draftKey = id && id !== 'new' ? `appraisal-draft-${id}` : null;

  useEffect(() => {
    if (!draftKey || !appraisal) return;
    const draft = { ratings, comments, starScores, employeeComment, acknowledged, savedAt: Date.now() };
    localStorage.setItem(draftKey, JSON.stringify(draft));
  }, [ratings, comments, starScores, employeeComment, acknowledged, draftKey, appraisal]);

  // Clear draft when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      if (draftKey) {
        localStorage.removeItem(draftKey);
      }
    };
  }, [draftKey]);

  const restoreDraft = useCallback((key, serverSavedAt) => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const draft = JSON.parse(saved);
        // A draft saved since (say, on another device) beats this older copy.
        if (serverSavedAt && (draft.savedAt || 0) < new Date(serverSavedAt).getTime()) return false;
        if (draft.ratings) setRatings(draft.ratings);
        if (draft.comments) setComments(draft.comments);
        if (draft.starScores) setStarScores(draft.starScores);
        if (draft.employeeComment) setEmployeeComment(draft.employeeComment);
        if (draft.acknowledged) setAcknowledged(draft.acknowledged);
        return true;
      }
    } catch { }
    return false;
  }, []);

  useEffect(() => {
    if (!isNew) loadAppraisal();
  }, [id]);

  const loadAppraisal = async () => {
    try {
      const response = await getAppraisalDetails(id);
      const appraisalData = response.data;

      setAppraisal({
        id: appraisalData._id,
        cycleName: appraisalData.cycle_name,
        employeeName: appraisalData.employee_name,
        employeeId: appraisalData?.employee_id?.employee_id,
        department: appraisalData.department,
        status: appraisalData.status,
        selfReview: appraisalData.self_review,
        hrReview: appraisalData.manager_review,
        employeeComments: appraisalData.employee_comments,
        adminReview: appraisalData.hr_review,
        hrSignOff: appraisalData.hr_sign_off,
        templateId: appraisalData.template_id?._id,
        cancelledAt: appraisalData.cancelled_at,
        cancelledBy: appraisalData.cancelled_by,
        cancellationReason: appraisalData.cancellation_reason,
      });

      // Set employee object from populated data
      if (appraisalData.employee_id && typeof appraisalData.employee_id === 'object') {
        setEmployee({
          employee_id: appraisalData.employee_id.employee_id,
          name: appraisalData.employee_id.name,
          designation: appraisalData.employee_id.designation,
          department: appraisalData.employee_id.department,
          manager: appraisalData.employee_id?.manager || '—',
          joinDate: appraisalData.employee_id.date_of_joining
            ? new Date(appraisalData.employee_id.date_of_joining).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })
            : '—'
        });
      }

      setTemplate(appraisalData.template_id);

      const savedDraft = appraisalData.my_draft;
      if (savedDraft) {
        const form = formFromDraft(savedDraft, appraisalData.template_id?.sections);
        setRatings(form.ratings);
        setComments(form.comments);
        setStarScores(form.starScores);
        // At this step the overall comment is the employee's final comment.
        if (appraisalData.status === "Employee Comment") setEmployeeComment(savedDraft.overall_comment || "");
        setDraftSavedAt(savedDraft.updatedAt);
      }
      restoreDraft(`appraisal-draft-${id}`, savedDraft?.updatedAt);
    } catch {
      toast.error("Failed to load appraisal");
    } finally {
      setLoading(false);
    }
  };

  const ratingOptions = template?.ratingOptions ? [...template.ratingOptions].reverse() : ["Exceeds Expectations", "Meets Expectations", "Needs Improvement", "Unacceptable"];
  const sections = template?.sections || [];
  const allQuestionIds = sections.flatMap(s => (s.questions || []).map(q => q.id));
  const answeredCount = Object.keys(ratings).filter(k => allQuestionIds.includes(k) && ratings[k]).length;
  const progressPct = allQuestionIds.length > 0 ? Math.round((answeredCount / allQuestionIds.length) * 100) : 0;

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draft = appraisal.status === "Employee Comment"
        ? { responses: [], overallComment: employeeComment }
        : draftFromForm({ ratings, comments, starScores }, allQuestionIds);
      const response = await saveAppraisalDraft(id, draft);
      setDraftSavedAt(response.data?.updatedAt || new Date());
      toast.success("Draft saved. Only you can see it until you submit.");
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitSelfReview = async () => {
    if (submitLockRef.current) return;

    if (!allQuestionIds.every(qId => ratings[qId])) {
      toast.error("Please rate all questions before submitting");
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const responses = allQuestionIds.map(qId => ({
        section_id: sections.find(s => s.questions?.find(q => q.id === qId))?.id,
        section_title: sections.find(s => s.questions?.find(q => q.id === qId))?.title,
        question_id: qId,
        question_text: sections.flatMap(s => s.questions || []).find(q => q.id === qId)?.text,
        rating: starScores[qId] || 0,
        rating_label: ratings[qId],
        comment: comments[qId] || "",
        star_score: starScores[qId] || 0
      }));

      const response = await submitAppraisalReview(id, 'self', {
        responses,
        overallComment: comments.overallComment || ""
      });
      setAppraisal(prev => ({ ...prev, status: "Approval Review", selfReview: response.data?.self_review || prev.selfReview }));
      setRatings({});
      setComments({});
      setStarScores({});
      if (draftKey) localStorage.removeItem(draftKey);
      toast.success("Self review submitted! Waiting for approval review.");
      setTimeout(() => navigate('/employee/reviews'), 1500);
    } catch (err) {
      submitLockRef.current = false;
      toast.error(err.response?.data?.error || err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEmployeeComments = async () => {
    if (submitLockRef.current) return;

    if (!acknowledged) {
      toast.error("Please acknowledge the review before submitting");
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      await submitAppraisalReview(id, 'employee', {
        accepted: acknowledged,
        comments: employeeComment
      });
      setAppraisal(prev => ({ ...prev, status: "Admin Review", employeeComments: { accepted: acknowledged, comments: employeeComment }}));
      if (draftKey) localStorage.removeItem(draftKey);
      toast.success("Comments submitted! Waiting for final approval.");
      setTimeout(() => navigate('/employee/reviews'), 1500);
    } catch (err) {
      submitLockRef.current = false;
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!appraisal) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p className="text-lg">Appraisal not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/employee/reviews')}>Back</Button>
      </div>
    );
  }

  const status = appraisal.status;
  const isCompleted = status === "Completed";
  const isCancelled = status === "Cancelled";

  const getSubmitAction = () => {
    switch (status) {
      // case "Pending": return { label: "Submit Self-Review", icon: Send, handler: handleSubmitSelfReview, color: "bg-primary hover:bg-primary/90" };
      case "Self Review":
        // Only show submit button if self-review hasn't been submitted yet
        const hasSubmittedSelfReview = appraisal.selfReview?.submitted_at || appraisal.selfReview?.responses?.length > 0;
        return hasSubmittedSelfReview
          ? null
          : { label: "Submit Self-Review", icon: Send, handler: handleSubmitSelfReview, color: "bg-primary hover:bg-primary/90" };
      case "Employee Comment": return { label: "Submit Final Comment", icon: MessageSquare, handler: handleSubmitEmployeeComments, color: "bg-primary hover:bg-primary/90" };
      default: return null;
    }
  };

  const submitAction = getSubmitAction();

  const handleQuestionChange = (qId, field, value) => {
    if (field === 'rating') setRatings(prev => ({ ...prev, [qId]: value }));
    if (field === 'starScore') setStarScores(prev => ({ ...prev, [qId]: value }));
    if (field === 'comment') setComments(prev => ({ ...prev, [qId]: value }));
  };

  return (
    <div className="max-w-7xl mx-auto pb-20">
      <AppraisalHeader
        appraisal={appraisal}
        status={status}
        submitAction={submitAction}
        draftAction={submitAction ? { handler: handleSaveDraft, saving: savingDraft, savedAt: draftSavedAt } : null}
        isCompleted={isCompleted}
        isEmployeeView={true}
        onBack={() => navigate(-1)}
        submitting={submitting}
        STEPS={STEPS}
        template={template}
        employee={employee}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <EmployeeInfoCard employee={employee} appraisal={appraisal} status={status} />
          </div>
        </div>

        <div className="lg:col-span-9 space-y-8">

          {isCancelled && (
            <>
              <Card className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20">
                <CardContent className="py-4 sm:py-6 flex items-start gap-3 sm:gap-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-amber-600 sm:h-8 sm:w-8 mt-0.5 shrink-0" />
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold leading-tight text-amber-700 dark:text-amber-300">Appraisal Cancelled</h3>
                      <p className="text-xs sm:text-sm leading-relaxed text-amber-700 dark:text-amber-300 mt-1">
                        This appraisal has been cancelled and is no longer active.
                      </p>
                      {appraisal.cancellationReason && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          <span className="font-medium">Reason : </span> {appraisal.cancellationReason}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(
                      r => r.question_id === question.id || r.questionId === question.id
                    );
                    const mgrResp = appraisal.hrReview?.responses?.find(
                      r => r.question_id === question.id || r.questionId === question.id
                    );

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        viewMode="employee"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.hrReview?.overall_comment} />
            </>
          )}

          {status === "Self Review" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Start Your Self-Review"
                message="Rate yourself on each question and provide comments to help your manager understand your achievements."
                statusFlow="Current: Self Review → Next: Approval Review (after submission)"
                icon="alert"
                variant="blue"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => (
                    <QuestionEditor
                      key={question.id}
                      question={question}
                      questionIndex={qi}
                      value={{
                        rating: ratings[question.id],
                        starScore: starScores[question.id],
                        comment: comments[question.id]
                      }}
                      onChange={handleQuestionChange}
                      ratingOptions={ratingOptions}
                    />
                  ))}
                </SectionCard>
              ))}
            </>
          )}

          {/* APPROVAL REVIEW: Waiting for manager review */}
          {status === "Approval Review" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Waiting for Manager Review"
                message="Your self-review has been submitted. Your manager is now reviewing your appraisal."
                statusFlow="Current: Approval Review → Next: Employee Comment (Manager action required)"
                icon="clock"
                variant="amber"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(
                      r => r.question_id === question.id || r.questionId === question.id
                    );

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={null} // No manager response yet
                        viewMode="employee"
                      />
                    );
                  })}
                </SectionCard>
              ))}
            </>
          )}

          {/* APPROVAL REVIEW: Read-only + final comment */}
          {status === "Employee Comment" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Provide Your Final Comment"
                message="Your manager has completed their review. Review their feedback below and provide your final comment."
                icon="alert"
                variant="purple"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.hrReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        viewMode="employee"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.hrReview?.overall_comment} />

              <Card className="bg-warning/30">
                <CardHeader className="border-b">
                  <CardTitle className="text-base text-warning flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" /> Your Final Comment
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Review both assessments above and share your final feedback.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <Textarea
                    placeholder="Share your thoughts on this review..."
                    className="min-h-[140px]"
                    value={employeeComment}
                    onChange={(e) => setEmployeeComment(e.target.value)}
                  />
                  <div className="flex items-start gap-2 p-4 bg-muted rounded-lg border">
                    <input
                      type="checkbox"
                      id="ack"
                      className="mt-1"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                    <label htmlFor="ack" className="text-sm text-muted-foreground cursor-pointer">
                      I acknowledge that I have reviewed this appraisal.
                    </label>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* EMPLOYEE COMMENT: Waiting for approval */}
          {status === "Admin Review" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Waiting for Final Admin Approval"
                message="Your final comment has been submitted successfully. The default admin will review it and complete the appraisal."
                statusFlow="Current: Admin Review -> Next: Completed (Admin action required)"
                icon="clock"
                variant="amber"
              />

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.hrReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        viewMode="employee"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.hrReview?.overall_comment} />

              {appraisal.employeeComments?.comments && (
                <Card className="border-success/30">
                  <CardHeader className="bg-success/5 border-b">
                    <CardTitle className="text-base text-success">Your Submitted Comment</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-sm text-muted-foreground italic">"{appraisal.employeeComments.comments}"</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* COMPLETED: Success view */}
          {status === "Completed" && sections.length > 0 && (
            <>
              <StatusBanner
                title="Appraisal Completed Successfully"
                message="This performance review has been completed and finalized by your manager."
                icon="check"
                variant="green"
              />

              {appraisal.hrSignOff?.finalRating && (
                <Card className="border-success/30">
                  <CardContent className="py-6">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Final Rating</p>
                      <p className="text-4xl font-bold text-success mt-2">{appraisal.hrSignOff.finalRating}/5</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {sections.map((section, sectionIndex) => (
                <SectionCard
                  key={section.id}
                  title={section.title}
                  description={section.description}
                  sectionIndex={sectionIndex}
                >
                  {(section.questions || []).map((question, qi) => {
                    const selfResp = appraisal.selfReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const mgrResp = appraisal.hrReview?.responses?.find(r => r.question_id === question.id || r.questionId === question.id);
                    const adminResp = appraisal.adminReview?.responses?.find(r => r.question_id === question.id);

                    return (
                      <QuestionDisplay
                        key={question.id}
                        question={question}
                        questionIndex={qi}
                        employeeResponse={selfResp}
                        managerResponse={mgrResp}
                        adminResponse={adminResp}
                        viewMode="employee"
                      />
                    );
                  })}
                </SectionCard>
              ))}

              <FinalCommentCard title="Manager's Final Comment" comment={appraisal.hrReview?.overall_comment} />
              <FinalCommentCard title="Your Final Comment" comment={appraisal.employeeComments?.comments} />
              <FinalCommentCard title="Admin's Final Comment" comment={appraisal.adminReview?.comments} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmployeeAppraisalEditor;
